import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import {
  Loader2, FileText, CheckCircle, Clock, Star, ExternalLink,
  Building2, Mail, Users, DollarSign, ChevronDown, ChevronUp, Award,
  ArrowLeft, Phone, Globe, Layers, Calendar, Paperclip, Download, Image as ImageIcon
} from 'lucide-react';
import { evaluatorsAPI } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EvaluatorDashboard = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();

  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [scores, setScores] = useState({});
  const [overallComment, setOverallComment] = useState('');
  const [saving, setSaving] = useState(false);

  const t = language === 'es' ? {
    title: 'Panel de Evaluador',
    subtitle: 'Aplicaciones asignadas por evaluar',
    nextToEval: 'POR EVALUAR',
    pending: 'Pendientes',
    completed: 'Completadas',
    noAssignments: 'No tienes aplicaciones asignadas',
    score: 'Evaluar',
    editScore: 'Editar',
    program: 'Desafio',
    criteria: 'Criterios de Evaluacion',
    overallComment: 'Comentario General',
    commentPlaceholder: 'Escribe tus observaciones generales...',
    submit: 'Enviar Evaluacion',
    update: 'Actualizar Evaluacion',
    cancel: 'Volver',
    success: 'Evaluacion enviada correctamente',
    error: 'Error al procesar',
    scored: 'Evaluada',
    totalScore: 'Puntaje Promedio',
    sectionPending: 'PENDIENTES',
    sectionCompleted: 'COMPLETADAS',
    noChallenge: 'Sin desafio asignado',
    teamSize: 'personas',
    applicationData: 'Datos de la Postulacion',
    founder: 'Fundador',
    email: 'Email',
    phone: 'Telefono',
    website: 'Sitio Web',
    stage: 'Etapa',
    team: 'Equipo',
    funding: 'Financiamiento',
    description: 'Descripcion',
    documents: 'Documentos',
    customFields: 'Campos Adicionales',
    submittedOn: 'Enviada el',
    backToList: 'Volver al listado',
    scoringPanel: 'Panel de Evaluacion',
    commentOptional: 'Comentario (opcional)',
  } : {
    title: 'Evaluator Dashboard',
    subtitle: 'Applications assigned for evaluation',
    nextToEval: 'NEXT TO EVALUATE',
    pending: 'Pending',
    completed: 'Completed',
    noAssignments: 'No applications assigned to you',
    score: 'Score',
    editScore: 'Edit',
    program: 'Challenge',
    criteria: 'Scoring Criteria',
    overallComment: 'Overall Comment',
    commentPlaceholder: 'Write your overall observations...',
    submit: 'Submit Score',
    update: 'Update Score',
    cancel: 'Back',
    success: 'Score submitted successfully',
    error: 'Error processing',
    scored: 'Scored',
    totalScore: 'Average Score',
    sectionPending: 'PENDING',
    sectionCompleted: 'COMPLETED',
    noChallenge: 'No challenge assigned',
    teamSize: 'people',
    applicationData: 'Application Data',
    founder: 'Founder',
    email: 'Email',
    phone: 'Phone',
    website: 'Website',
    stage: 'Stage',
    team: 'Team',
    funding: 'Funding Raised',
    description: 'Description',
    documents: 'Documents',
    customFields: 'Additional Fields',
    submittedOn: 'Submitted on',
    backToList: 'Back to list',
    scoringPanel: 'Scoring Panel',
    commentOptional: 'Comment (optional)',
  };

  useEffect(() => {
    if (user) fetchAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const userId = user._id || user.id;
      const data = await evaluatorsAPI.getMyAssignments(userId);
      setAssignments(data);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      toast({ title: t.error, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const openScoring = (assignment) => {
    setSelectedAssignment(assignment);
    const defaultCriteria = language === 'es' ? [
      { name: 'Innovacion', description: 'Nivel de innovacion y originalidad', maxScore: 10 },
      { name: 'Viabilidad', description: 'Viabilidad tecnica y comercial', maxScore: 10 },
      { name: 'Equipo', description: 'Capacidades y experiencia del equipo', maxScore: 10 },
      { name: 'Impacto', description: 'Potencial de impacto', maxScore: 10 },
    ] : [
      { name: 'Innovation', description: 'Level of innovation', maxScore: 10 },
      { name: 'Feasibility', description: 'Technical and commercial feasibility', maxScore: 10 },
      { name: 'Team', description: 'Team capabilities', maxScore: 10 },
      { name: 'Impact', description: 'Potential impact', maxScore: 10 },
    ];
    const criteria = assignment.program?.scoringCriteria?.length > 0
      ? assignment.program.scoringCriteria : defaultCriteria;
    const initialScores = {};
    criteria.forEach(c => {
      const existing = assignment.score?.scores?.find(s => s.criterionName === c.name);
      initialScores[c.name] = { score: existing?.score || 5, comment: existing?.comment || '' };
    });
    setScores(initialScores);
    setOverallComment(assignment.score?.overallComment || '');
  };

  const handleSubmitScore = async () => {
    try {
      setSaving(true);
      const scoreData = {
        applicationId: selectedAssignment.applicationId,
        evaluatorId: user._id || user.id,
        programId: selectedAssignment.programId,
        scores: Object.entries(scores).map(([name, data]) => ({
          criterionName: name, score: data.score, comment: data.comment || null
        })),
        overallComment: overallComment || null
      };
      if (selectedAssignment.hasScored && selectedAssignment.score) {
        await evaluatorsAPI.updateScore(selectedAssignment.score._id, scoreData);
      } else {
        await evaluatorsAPI.submitScore(scoreData);
      }
      toast({ title: t.success });
      setSelectedAssignment(null);
      fetchAssignments();
    } catch (error) {
      console.error('Error submitting score:', error);
      toast({ title: t.error, description: error.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const pendingAssignments = assignments.filter(a => !a.hasScored);
  const completedAssignments = assignments.filter(a => a.hasScored);
  const nextChallenge = pendingAssignments[0]?.program?.name || t.noChallenge;

  const stageLabels = {
    idea: language === 'es' ? 'Idea' : 'Idea',
    prototype: language === 'es' ? 'Prototipo' : 'Prototype',
    mvp: 'MVP',
    'early-revenue': language === 'es' ? 'Ingresos tempranos' : 'Early Revenue',
    growth: language === 'es' ? 'Crecimiento' : 'Growth',
  };

  // Helper to extract URL from a value (handles both string URLs and {url, name, type} objects)
  const getFileUrl = (value) => {
    if (typeof value === 'object' && value !== null && value.url) return value.url;
    if (typeof value === 'string' && value.startsWith('http')) return value;
    return null;
  };

  const getFileName = (value) => {
    if (typeof value === 'object' && value !== null && value.name) return value.name;
    return null;
  };

  const isFileValue = (value) => {
    const url = getFileUrl(value);
    if (!url) return false;
    return url.includes('/uploads/') ||
      /\.(pdf|doc|docx|xlsx|ppt|pptx|zip)(\?|$)/i.test(url);
  };

  const isImageValue = (value) => {
    const url = getFileUrl(value);
    if (!url) return false;
    return /\.(png|jpg|jpeg|webp|gif)(\?|$)/i.test(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#eef0f4] flex justify-center items-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  // ===================== SPLIT-SCREEN SCORING VIEW =====================
  if (selectedAssignment) {
    const app = selectedAssignment.application || {};
    const program = selectedAssignment.program || {};
    const customFields = program.customFormFields || [];
    const customData = app.customFieldsData || {};
    const criteria = program.scoringCriteria?.length > 0
      ? program.scoringCriteria
      : (language === 'es'
        ? [{ name: 'Innovacion', maxScore: 10 }, { name: 'Viabilidad', maxScore: 10 }, { name: 'Equipo', maxScore: 10 }, { name: 'Impacto', maxScore: 10 }]
        : [{ name: 'Innovation', maxScore: 10 }, { name: 'Feasibility', maxScore: 10 }, { name: 'Team', maxScore: 10 }, { name: 'Impact', maxScore: 10 }]
      );

    return (
      <div className="min-h-screen bg-[#eef0f4]" data-testid="evaluator-scoring-view">
        {/* Top bar */}
        <div className="bg-white border-b border-gray-200 sticky top-16 z-40">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
            <button
              onClick={() => setSelectedAssignment(null)}
              className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
              data-testid="back-to-list-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              {t.backToList}
            </button>
            <div className="text-center hidden sm:block">
              <p className="text-sm font-semibold text-gray-900">{app.startupName}</p>
              <p className="text-xs text-gray-500">{program.name}</p>
            </div>
            <Badge className={selectedAssignment.hasScored ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
              {selectedAssignment.hasScored ? t.scored : t.pending}
            </Badge>
          </div>
        </div>

        {/* Split layout */}
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
          <div className="flex flex-col lg:flex-row gap-6">

            {/* LEFT: Application Data */}
            <div className="lg:w-[55%] space-y-5" data-testid="application-panel">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-500" />
                {t.applicationData}
              </h2>

              {/* Basic Info Card */}
              <Card className="border border-gray-200">
                <CardContent className="p-5 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">{t.founder}</p>
                      <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        {app.founderName || '—'}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">{t.email}</p>
                      <p className="text-sm text-gray-700 flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        {app.email || '—'}
                      </p>
                    </div>
                    {app.phone && (
                      <div className="space-y-1">
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">{t.phone}</p>
                        <p className="text-sm text-gray-700 flex items-center gap-2">
                          <Phone className="w-4 h-4 text-gray-400" />
                          {app.phone}
                        </p>
                      </div>
                    )}
                    {app.website && (
                      <div className="space-y-1">
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">{t.website}</p>
                        <a href={app.website.startsWith('http') ? app.website : `https://${app.website}`}
                          target="_blank" rel="noopener noreferrer"
                          className="text-sm text-purple-600 hover:underline flex items-center gap-2">
                          <Globe className="w-4 h-4 text-gray-400" />
                          {app.website}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    )}
                    <div className="space-y-1">
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">{t.stage}</p>
                      <p className="text-sm text-gray-700 flex items-center gap-2">
                        <Layers className="w-4 h-4 text-gray-400" />
                        {stageLabels[app.stage] || app.stage || '—'}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">{t.team}</p>
                      <p className="text-sm text-gray-700 flex items-center gap-2">
                        <Users className="w-4 h-4 text-gray-400" />
                        {app.teamSize || 0} {t.teamSize}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">{t.funding}</p>
                      <p className="text-sm text-gray-700 flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-gray-400" />
                        ${(app.fundingRaised || 0).toLocaleString()}
                      </p>
                    </div>
                    {app.submittedDate && (
                      <div className="space-y-1">
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">{t.submittedOn}</p>
                        <p className="text-sm text-gray-700 flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {new Date(app.submittedDate).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Description */}
              {app.description && (
                <Card className="border border-gray-200">
                  <CardContent className="p-5">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">{t.description}</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{app.description}</p>
                  </CardContent>
                </Card>
              )}

              {/* Pitch Deck / Documents */}
              {app.pitchDeck && (
                <Card className="border border-gray-200">
                  <CardContent className="p-5">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">{t.documents}</p>
                    <a href={app.pitchDeck.startsWith('http') ? app.pitchDeck : `${API_URL}${app.pitchDeck}`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-purple-50 border border-purple-200 rounded-lg text-sm font-medium text-purple-700 hover:bg-purple-100 transition-colors"
                      data-testid="pitch-deck-link"
                    >
                      <Download className="w-4 h-4" />
                      Pitch Deck / {language === 'es' ? 'Documento' : 'Document'}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </CardContent>
                </Card>
              )}

              {/* Custom Form Fields */}
              {Object.keys(customData).length > 0 && (
                <Card className="border border-gray-200">
                  <CardContent className="p-5">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-4">{t.customFields}</p>
                    <div className="space-y-4">
                      {customFields.length > 0 ? (
                        customFields.map((field) => {
                          const value = customData[field.id];
                          if (value === undefined || value === null || value === '') return null;

                          return (
                            <div key={field.id} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0" data-testid={`custom-field-${field.id}`}>
                              <p className="text-xs font-semibold text-gray-500 mb-1">{field.label}</p>
                              {isImageValue(value) ? (
                                <div>
                                  <img src={getFileUrl(value)} alt={field.label} className="max-w-xs rounded-lg border border-gray-200 mt-1" />
                                  <a href={getFileUrl(value)} target="_blank" rel="noopener noreferrer"
                                    className="text-xs text-purple-600 hover:underline flex items-center gap-1 mt-1">
                                    <ExternalLink className="w-3 h-3" /> {language === 'es' ? 'Ver original' : 'View original'}
                                  </a>
                                </div>
                              ) : isFileValue(value) || (typeof value === 'object' && value?.url) ? (
                                <a href={getFileUrl(value)} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-purple-700 hover:bg-gray-100 transition-colors">
                                  <Paperclip className="w-4 h-4" />
                                  {getFileName(value) || (language === 'es' ? 'Descargar archivo' : 'Download file')}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              ) : typeof value === 'boolean' ? (
                                <p className="text-sm text-gray-700">{value ? (language === 'es' ? 'Si' : 'Yes') : 'No'}</p>
                              ) : (
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{String(value)}</p>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        Object.entries(customData).map(([key, value]) => {
                          if (value === undefined || value === null || value === '') return null;
                          return (
                            <div key={key} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0" data-testid={`custom-field-${key}`}>
                              <p className="text-xs font-semibold text-gray-500 mb-1">{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
                              {isImageValue(value) ? (
                                <div>
                                  <img src={getFileUrl(value)} alt={key} className="max-w-xs rounded-lg border border-gray-200 mt-1" />
                                  <a href={getFileUrl(value)} target="_blank" rel="noopener noreferrer"
                                    className="text-xs text-purple-600 hover:underline flex items-center gap-1 mt-1">
                                    <ExternalLink className="w-3 h-3" /> {language === 'es' ? 'Ver original' : 'View original'}
                                  </a>
                                </div>
                              ) : isFileValue(value) || (typeof value === 'object' && value?.url) ? (
                                <a href={getFileUrl(value)} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-purple-700 hover:bg-gray-100 transition-colors">
                                  <Paperclip className="w-4 h-4" />
                                  {getFileName(value) || (language === 'es' ? 'Descargar archivo' : 'Download file')}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              ) : typeof value === 'boolean' ? (
                                <p className="text-sm text-gray-700">{value ? (language === 'es' ? 'Si' : 'Yes') : 'No'}</p>
                              ) : (
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{String(value)}</p>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* RIGHT: Scoring Panel */}
            <div className="lg:w-[45%]" data-testid="scoring-panel">
              <div className="lg:sticky lg:top-32 space-y-5">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-500" />
                  {t.scoringPanel}
                </h2>

                {/* Criteria */}
                <div className="space-y-4">
                  {criteria.map((c) => (
                    <Card key={c.name} className="border border-gray-200" data-testid={`criterion-card-${c.name}`}>
                      <CardContent className="p-4 space-y-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <Label className="font-semibold text-sm">{c.name}</Label>
                            {c.description && <p className="text-xs text-gray-500 mt-0.5">{c.description}</p>}
                          </div>
                          <span className="text-xl font-bold text-purple-600 shrink-0 ml-3">
                            {scores[c.name]?.score || 0}<span className="text-gray-400 text-xs">/{c.maxScore || 10}</span>
                          </span>
                        </div>
                        <div className="flex gap-1">
                          {Array.from({ length: c.maxScore || 10 }, (_, i) => i + 1).map((num) => (
                            <button key={num} type="button"
                              onClick={() => setScores({ ...scores, [c.name]: { ...scores[c.name], score: num } })}
                              className={`flex-1 h-8 rounded font-semibold text-xs transition-all ${
                                scores[c.name]?.score === num
                                  ? 'bg-purple-600 text-white shadow-md scale-105'
                                  : num <= (scores[c.name]?.score || 0)
                                    ? 'bg-purple-100 text-purple-700'
                                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                              }`}
                              data-testid={`score-btn-${c.name}-${num}`}
                            >{num}</button>
                          ))}
                        </div>
                        <Input
                          placeholder={t.commentOptional}
                          value={scores[c.name]?.comment || ''}
                          onChange={(e) => setScores({ ...scores, [c.name]: { ...scores[c.name], comment: e.target.value } })}
                          className="text-sm"
                          data-testid={`comment-input-${c.name}`}
                        />
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Average */}
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-purple-800 text-sm">{t.totalScore}</span>
                    <span className="text-2xl font-bold text-purple-600">
                      {(() => {
                        const vals = Object.values(scores).map(s => s.score || 0);
                        return vals.length > 0 ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : 0;
                      })()} / 10
                    </span>
                  </div>
                </div>

                {/* Overall Comment */}
                <div className="space-y-2">
                  <Label className="font-semibold text-sm">{t.overallComment}</Label>
                  <Textarea
                    value={overallComment}
                    onChange={(e) => setOverallComment(e.target.value)}
                    placeholder={t.commentPlaceholder}
                    rows={3}
                    data-testid="overall-comment-textarea"
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setSelectedAssignment(null)}
                    className="flex-1"
                  >
                    {t.cancel}
                  </Button>
                  <Button
                    onClick={handleSubmitScore}
                    disabled={saving}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                    data-testid="submit-score-btn"
                  >
                    {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    {selectedAssignment?.hasScored ? t.update : t.submit}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===================== LIST VIEW =====================
  const AssignmentRow = ({ assignment, isCompleted }) => {
    const app = assignment.application || {};
    return (
      <div
        className="glass-card rounded-xl mb-3 overflow-hidden cursor-pointer hover:ring-2 hover:ring-purple-300 transition-all"
        data-testid={`assignment-${assignment._id}`}
        onClick={() => openScoring(assignment)}
      >
        <div className="p-5 flex items-center justify-between">
          <div className="flex items-center gap-4 min-w-0">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
              isCompleted ? 'bg-emerald-100' : 'bg-amber-100'
            }`}>
              {isCompleted
                ? <CheckCircle className="w-5 h-5 text-emerald-600" />
                : <Clock className="w-5 h-5 text-amber-600" />
              }
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 truncate">{app.startupName || '—'}</p>
              <p className="text-sm text-gray-500 truncate">{assignment.program?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {isCompleted && assignment.score && (
              <Badge className="bg-emerald-100 text-emerald-700 text-sm">
                <Star className="w-3.5 h-3.5 mr-1 fill-emerald-500" />
                {assignment.score.weightedScore?.toFixed(1) || assignment.score.totalScore?.toFixed(1)}
              </Badge>
            )}
            <Button
              size="sm"
              className={isCompleted ? 'bg-gray-700 hover:bg-gray-800 text-xs' : 'bg-purple-600 hover:bg-purple-700 text-xs'}
              onClick={(e) => { e.stopPropagation(); openScoring(assignment); }}
              data-testid={`score-btn-${assignment._id}`}
            >
              <Star className="w-3.5 h-3.5 mr-1" />
              {isCompleted ? t.editScore : t.score}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#eef0f4]" data-testid="evaluator-dashboard">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t.title}</h1>
          <p className="text-gray-500 mt-1">{t.subtitle}</p>
        </div>

        {/* 3 Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <Card className="glass-card border-none" data-testid="next-eval-card">
            <CardContent className="p-6 sm:p-10">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">{t.nextToEval}</p>
              <p className="text-xl sm:text-2xl font-bold text-purple-700 truncate">{nextChallenge}</p>
              <Award className="w-8 h-8 text-purple-300 mt-4" />
            </CardContent>
          </Card>

          <Card className="glass-card border-none" data-testid="pending-count-card">
            <CardContent className="p-6 sm:p-10">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">{t.pending}</p>
              <p className="text-[60px] sm:text-[90px] lg:text-[120px] font-black text-amber-600 leading-none">{pendingAssignments.length}</p>
              <Clock className="w-8 h-8 text-amber-300 mt-4" />
            </CardContent>
          </Card>

          <Card className="glass-card border-none" data-testid="completed-count-card">
            <CardContent className="p-6 sm:p-10">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">{t.completed}</p>
              <p className="text-[60px] sm:text-[90px] lg:text-[120px] font-black text-emerald-600 leading-none">{completedAssignments.length}</p>
              <CheckCircle className="w-8 h-8 text-emerald-300 mt-4" />
            </CardContent>
          </Card>
        </div>

        {assignments.length === 0 ? (
          <Card className="glass-card border-none text-center py-16">
            <CardContent>
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-600">{t.noAssignments}</h3>
            </CardContent>
          </Card>
        ) : (
          <>
            {pendingAssignments.length > 0 && (
              <div className="mb-10">
                <p className="text-xs font-semibold uppercase tracking-widest text-amber-600 mb-4">{t.sectionPending}</p>
                {pendingAssignments.map(a => <AssignmentRow key={a._id} assignment={a} isCompleted={false} />)}
              </div>
            )}
            {completedAssignments.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600 mb-4">{t.sectionCompleted}</p>
                {completedAssignments.map(a => <AssignmentRow key={a._id} assignment={a} isCompleted={true} />)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EvaluatorDashboard;
